import { Skeleton } from '@mantine/core';
import type { KeyboardEvent } from 'react';
import { IconSearch, IconMenu2, IconGift } from '@tabler/icons-react';
import { useMediaQuery } from 'react-responsive';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';

import { getReferralCode } from '~/domains/analytics/utils';
import { URLQueryKeys } from '~/domains/common/types/URLQueryKeys';
import { CurrencyConvertor } from '~/components/utils/formatters';
import { Routes } from '~/domains/common/constants/routes';
import { Text } from '~/domains/design-system/Text';
import { UnstyledButton, UnstyledButtonLink } from '~/domains/design-system/Button';
import { Avatar } from '~/domains/design-system/Avatar';
import { isPathMatch } from '~/utils/isPathMatch';
import dt from '~/testing';

import type { AppShellHeaderProps } from '../../types';

import IconPlus from './assets/IconPlus';
import useStyles from './Header.styles';

function UserPill({ user, handleDepositClick, handleUserClick }: AppShellHeaderProps) {
  const { cx, classes, theme } = useStyles();
  const { t } = useTranslation('common');
  const isSmallMobile = useMediaQuery({ maxWidth: 350 });
  const router = useRouter();

  const notificationsCount = user?.notificationsCount;
  const currentPath = router.asPath;
  const isPromotionsActive = isPathMatch(currentPath, Routes.promotions());

  const [asPathname, asQueryString] = router.asPath.split('?');
  const query = new URLSearchParams(asQueryString || '');
  // Preserve the original redirectTo if it exists
  const redirectToOriginal = query.get(URLQueryKeys.REDIRECT_TO);
  const referralCode = query.get(URLQueryKeys.REFERRAL_CODE) || getReferralCode();
  // We need to remove the referral code from the query to avoid it being passed in the redirectTo
  query.delete(URLQueryKeys.REFERRAL_CODE);
  const remainingQuery = query.toString();
  const redirectTo = redirectToOriginal || `${asPathname}${remainingQuery ? `?${remainingQuery}` : ''}`;

  const handleUserKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleUserClick();
    }
  };

  return user ? (
    <div className={classes.userContainer}>
      <UnstyledButtonLink className={classes.searchButton} href={Routes.search()}>
        <IconSearch size={20} />
      </UnstyledButtonLink>

      <UnstyledButtonLink
        href={Routes.createAContest()}
        className={cx(classes.primaryButton, classes.cacButton)}
        data-test-id={dt.contest.create.action.cta}
      >
        <IconPlus color={theme.colors.gray2[6]} size={12} />
        <Text variant="subhead-medium">{t('appShellHeader3.userWidget.cacLabel')}</Text>
      </UnstyledButtonLink>

      <div className={classes.divider} />

      <div className={classes.promotionsButtonContainer}>
        <div
          className={cx(classes.promotionsBar, isPromotionsActive && classes.promotionsBarActive)}
        />
        <UnstyledButtonLink
          href={Routes.promotions()}
          className={cx(classes.searchButton, isPromotionsActive && classes.promotionsActive)}
          data-test-id={dt.common.components.appShellHeader.promotionsButton}
        >
          <IconGift size={20} color={isPromotionsActive ? theme.colors.blue[4] : theme.white} />
        </UnstyledButtonLink>
      </div>

      <div
        className={classes.userWidgetContainer}
        onClick={handleUserClick}
        onKeyDown={handleUserKeyDown}
        role="button"
        tabIndex={0}
        data-test-id={dt.common.components.appShellHeader.userMenuButton}
      >
        <Avatar size={28} src={user?.avatar} />
        {notificationsCount > 0 && (
          <div
            className={cx(classes.notificationBadge, [
              notificationsCount > 9 && 'isWide',
              notificationsCount > 100 && 'isCapped',
            ])}
          >
            <Text
              variant="headline-x-small"
              data-test-id={dt.common.components.appShellHeader.notificationsCounter}
            >
              {notificationsCount > 99 ? '99+' : notificationsCount}
            </Text>
          </div>
        )}
        <Text
          variant="subhead-medium"
          className={classes.balance}
          data-test-id={dt.common.components.appShellHeader.balance}
        >
          {user.isBalanceLoading ? (
            <Skeleton className={classes.balanceSkeleton} />
          ) : (
            CurrencyConvertor(user.balance, true)
          )}
        </Text>
        <UnstyledButton
          onClick={(event) => {
            event.stopPropagation();
            handleDepositClick(event);
          }}
          className={classes.depositButton}
          data-test-id={dt.common.components.appShellHeader.depositButton}
        >
          <IconPlus color={theme.white} />
        </UnstyledButton>
        <UnstyledButton
          onClick={handleUserClick}
          className={classes.mobileMenuButton}
          data-test-id={dt.common.components.appShellHeader.mobileMenuButton}
        >
          <IconMenu2 size={20} />
        </UnstyledButton>
      </div>
    </div>
  ) : (
    <div className={classes.userContainer}>
      <UnstyledButtonLink
        href={Routes.createAContest()}
        className={cx(classes.primaryButton, classes.cacButton, classes.cacButtonAnonymous)}
        data-test-id={dt.components.shared.header.contestCreateButton}
      >
        <IconPlus color={theme.white} size={12} />
        <Text variant="subhead-medium">{t('appShellHeader3.userWidget.cacLabel')}</Text>
      </UnstyledButtonLink>

      <UnstyledButtonLink
        className={cx(classes.primaryButton, classes.authButton)}
        href={Routes.signIn({
          redirectTo,
          referralCode,
        })}
      >
        <Text data-test-id={dt.common.components.appShellHeader.userPill} variant="subhead-medium">
          {isSmallMobile
            ? t('appShellHeader3.userWidget.authLabelMobile')
            : t('appShellHeader3.userWidget.authLabel')}
        </Text>
      </UnstyledButtonLink>
      <UnstyledButton
        className={cx(classes.mobileMenuButton, 'standalone')}
        onClick={handleUserClick}
        data-test-id={dt.common.components.appShellHeader.mobileMenuButton}
      >
        <IconMenu2 size={20} />
      </UnstyledButton>
    </div>
  );
}

export default UserPill;
